// Canonical value set for the encounter Claim Status tag
// (`…/CodeSystem/encounter-billing-status`).
//
// This lives in a `*.contract.ts` file (not a plain module) so the oRPC billing
// contracts can import it: the web bundle's package-boundary guard only lets
// `*.contract.ts` / `contracts.ts` cross from middleware. `./billing-status.constants`
// re-exports it for the class-validator DTOs and handlers.
//
// The code-system URL is unchanged. The two original codes (`pending`, `billed`) are
// retained so existing encounters keep a valid status with no data rewrite; they are
// simply relabeled — `pending → Pending Coding`, `billed → Submitted` — which is how
// existing data "migrates" to the seven-value Claim Status workflow. The other five
// codes are new — including `failed`, set only by the claim-submission pipeline when a
// submission run itself errors (never by hand; see MANUAL_BILLING_STATUS_VALUES below).
// Transitions are enforced: `pending`/`ready-to-bill`/`paid`/`failed` are automatic
// (see billing-v2/claim-status-transitions.ts), users may only set the MANUAL_* subset.
export const BILLING_STATUS_VALUES = [
  'pending',
  'ready-to-bill',
  'billed',
  'failed',
  'paid',
  'denied',
  'voided',
] as const

export type BillingStatusValue = (typeof BILLING_STATUS_VALUES)[number]

// The only statuses a user may set by hand — everything else is automatic:
// `pending`/`ready-to-bill` follow the claim's CPT lines, `paid` follows the payments,
// `failed` is system-set only (see CaseRepository.recordFailed) — recovery is re-marking
// the case `billed` ("Submitted") by hand, which re-triggers submission.
export const MANUAL_BILLING_STATUS_VALUES = ['billed', 'denied', 'voided'] as const

export type ManualBillingStatusValue = (typeof MANUAL_BILLING_STATUS_VALUES)[number]

export const BILLING_STATUS_LABELS: Record<BillingStatusValue, string> = {
  pending: 'Pending Coding',
  'ready-to-bill': 'Ready to Bill',
  billed: 'Submitted',
  failed: 'Failed',
  paid: 'Paid',
  denied: 'Denied',
  voided: 'Voided',
}
